import React, { useState, useEffect, useCallback } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DollarSign, FileText, Users, Plus, Search, Filter,
  Eye, Send, CheckCircle, Clock, AlertTriangle,
  TrendingUp, Download, ArrowLeft, BarChart3, Settings2, Trash2,
  Upload, Image, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingPage() {
  const { get, post, token } = useAdminApi();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, clientRes, dashRes] = await Promise.all([
        get('/billing/invoices'),
        get('/billing/clients'),
        get('/billing/dashboard'),
      ]);
      if (invRes.success) setInvoices(invRes.data);
      if (clientRes.success) setClients(clientRes.data);
      if (dashRes.success) setDashboard(dashRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [get]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusConfig = {
    draft: { label: 'Brouillon', color: 'text-slate-400', bg: 'bg-slate-500/10', icon: FileText },
    sent: { label: 'Envoyee', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Send },
    paid: { label: 'Payee', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
    overdue: { label: 'En retard', color: 'text-red-400', bg: 'bg-red-500/10', icon: AlertTriangle },
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter !== 'all' && inv.status !== filter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (inv.invoice_number || '').toLowerCase().includes(term) ||
        (inv.client_name || '').toLowerCase().includes(term) ||
        (inv.driver_name || '').toLowerCase().includes(term)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Facturation</h1>
          <p className="text-slate-400 text-sm mt-1">Gerez vos factures et suivez vos revenus</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={() => setActiveTab('clients')}
            data-testid="billing-clients-btn"
          >
            <Users size={16} className="mr-2" /> Clients
          </Button>
          <Button
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={() => setActiveTab('taxconfig')}
            data-testid="billing-taxconfig-btn"
          >
            <Settings2 size={16} className="mr-2" /> Taxes
          </Button>
          <Button
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={() => setActiveTab('invoiceconfig')}
            data-testid="billing-invoiceconfig-btn"
          >
            <Image size={16} className="mr-2" /> Config Facture
          </Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-black"
            onClick={() => setActiveTab('new')}
            data-testid="create-invoice-btn"
          >
            <Plus size={16} className="mr-2" /> Nouvelle facture
          </Button>
        </div>
      </div>

      {/* Tab routing */}
      {activeTab === 'dashboard' && (
        <>
          <DashboardTab dashboard={dashboard} invoices={invoices} statusConfig={statusConfig} />
          {/* Invoice list directly on main page */}
          {invoices.length > 0 && (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Rechercher facture, client..."
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    data-testid="invoice-search"
                  />
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  {['all', 'draft', 'sent', 'paid', 'overdue'].map(f => {
                    const count = f === 'all' ? invoices.length : invoices.filter(i => i.status === f).length;
                    return (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                          filter === f ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                        data-testid={`filter-${f}`}
                      >
                        {f === 'all' ? 'Tous' : statusConfig[f]?.label}
                        {count > 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                            f === 'draft' && count > 0 && filter !== f
                              ? 'bg-orange-500 text-white'
                              : filter === f ? 'bg-black/20 text-black' : 'bg-slate-700 text-slate-300'
                          }`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                    onClick={async () => {
                      try {
                        const res = await post('/billing/auto-generate-drafts-batch', {});
                        if (res.success) {
                          const created = res.data.processed.reduce((s, p) => s + (p.created || 0), 0);
                          toast.success(`${created} brouillon(s) genere(s) sur ${res.data.total_reports_scanned} rapport(s)`);
                          fetchData();
                        }
                      } catch (e) {
                        toast.error('Erreur lors de la generation');
                      }
                    }}
                    data-testid="auto-generate-batch-btn"
                    title="Genere automatiquement les brouillons pour les rapports completes sans facture"
                  >
                    <Sparkles size={14} className="mr-1" />
                    Générer brouillons manquants
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {filteredInvoices.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune facture trouvee</p>
                  </div>
                ) : (
                  filteredInvoices.map(inv => {
                    const sc = statusConfig[inv.status] || statusConfig.draft;
                    const Icon = sc.icon;
                    return (
                      <div
                        key={inv.id}
                        className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 hover:border-slate-600 transition-colors cursor-pointer"
                        onClick={() => setActiveTab(`detail-${inv.id}`)}
                        data-testid={`invoice-row-${inv.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${sc.bg}`}>
                              <Icon size={18} className={sc.color} />
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{inv.custom_invoice_number || inv.invoice_number}</p>
                              <p className="text-slate-400 text-xs">{inv.client_name || 'Client non defini'} — {inv.date}</p>
                              {inv.line_items?.[0]?.description && (
                                <p className="text-slate-500 text-xs mt-0.5 truncate max-w-md">{inv.line_items[0].description}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-bold">{inv.total?.toFixed(2)} $</p>
                            <span className={`text-xs ${sc.color}`}>{sc.label}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </>
      )}
      {activeTab === 'clients' && <ClientsTab get={get} post={post} token={token} clients={clients} onRefresh={fetchData} onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'taxconfig' && <TaxConfigTab get={get} token={token} onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'invoiceconfig' && <InvoiceConfigTab get={get} token={token} onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'new' && <NewInvoiceTab get={get} post={post} token={token} clients={clients} onDone={() => { setActiveTab('dashboard'); fetchData(); }} onBack={() => setActiveTab('dashboard')} />}
      {activeTab === 'invoices' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Rechercher facture, client..."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                data-testid="invoice-search"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'draft', 'sent', 'paid', 'overdue'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    filter === f ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                  data-testid={`filter-${f}`}
                >
                  {f === 'all' ? 'Tous' : statusConfig[f]?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Invoice list */}
          <div className="space-y-2">
            {filteredInvoices.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText size={40} className="mx-auto mb-3 opacity-50" />
                <p>Aucune facture trouvee</p>
              </div>
            ) : (
              filteredInvoices.map(inv => {
                const sc = statusConfig[inv.status] || statusConfig.draft;
                const Icon = sc.icon;
                return (
                  <div
                    key={inv.id}
                    className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 hover:border-slate-600 transition-colors cursor-pointer"
                    onClick={() => setActiveTab(`detail-${inv.id}`)}
                    data-testid={`invoice-row-${inv.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${sc.bg}`}>
                          <Icon size={18} className={sc.color} />
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{inv.custom_invoice_number || inv.invoice_number}</p>
                          <p className="text-slate-400 text-xs">{inv.client_name || 'Client non defini'} — {inv.date}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{inv.total?.toFixed(2)} $</p>
                        <span className={`text-xs ${sc.color}`}>{sc.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
      {activeTab.startsWith('detail-') && (
        <InvoiceDetailTab
          invoiceId={activeTab.replace('detail-', '')}
          get={get}
          post={post}
          token={token}
          clients={clients}
          onBack={() => { setActiveTab('dashboard'); fetchData(); }}
          statusConfig={statusConfig}
        />
      )}
    </div>
  );
}

// ============================
// DASHBOARD TAB
// ============================
function DashboardTab({ dashboard, invoices, statusConfig }) {
  if (!dashboard) return null;
  const stats = [
    { label: 'Total facture', value: `${dashboard.total_invoiced?.toFixed(2)} $`, icon: DollarSign, color: 'text-white' },
    { label: 'Paye', value: `${dashboard.total_paid?.toFixed(2)} $`, icon: CheckCircle, color: 'text-green-400' },
    { label: 'En attente', value: `${dashboard.total_pending?.toFixed(2)} $`, icon: Clock, color: 'text-blue-400' },
    { label: 'En retard', value: `${dashboard.total_overdue?.toFixed(2)} $`, icon: AlertTriangle, color: 'text-red-400' },
  ];

  return (
    <div className="space-y-6" data-testid="billing-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={16} className={s.color} />
              <span className="text-xs text-slate-400">{s.label}</span>
            </div>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Recent invoices */}
      <div>
        <h3 className="text-white font-semibold mb-3">Factures recentes</h3>
        {invoices.slice(0, 5).map(inv => {
          const sc = statusConfig[inv.status] || statusConfig.draft;
          return (
            <div key={inv.id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
              <div>
                <span className="text-sm text-white">{inv.custom_invoice_number || inv.invoice_number}</span>
                <span className="text-xs text-slate-500 ml-2">{inv.client_name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs ${sc.color}`}>{sc.label}</span>
                <span className="text-sm font-medium text-white">{inv.total?.toFixed(2)} $</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================
// CLIENTS TAB
// ============================
function ClientsTab({ get, post, token, clients: initialClients, onRefresh, onBack }) {
  const [clients, setClients] = useState(initialClients || []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', province: 'QC',
    postal_code: '', tps_number: '', tvq_number: '', is_carrier: false,
    default_rate_type: 'per_km', default_rate_value: 0, default_stop_rate: 0,
    fuel_surcharge_pct: 0, payment_terms_days: 30, rate_presets: [],
  });
  const [editId, setEditId] = useState(null);

  const rateTypes = [
    { value: 'per_km', label: 'Au kilometre' },
    { value: 'per_km_stops', label: 'Au km + arrets' },
    { value: 'per_trip', label: 'Au voyage' },
    { value: 'per_hour', label: "A l'heure" },
    { value: 'flat', label: 'Forfaitaire' },
  ];

  const handleSave = async () => {
    try {
      const url = editId ? `/billing/clients/${editId}` : '/billing/clients';
      const method = editId ? 'put' : 'post';
      await axios[method](`${API_URL}/api${url}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(editId ? 'Client modifie' : 'Client cree');
      setShowForm(false);
      setEditId(null);
      onRefresh();
      const updatedClients = await get('/billing/clients');
      if (updatedClients.success) setClients(updatedClients.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (client) => {
    setForm({
      name: client.name || '', email: client.email || '', phone: client.phone || '',
      address: client.address || '', city: client.city || '', province: client.province || 'QC',
      postal_code: client.postal_code || '', tps_number: client.tps_number || '',
      tvq_number: client.tvq_number || '', is_carrier: client.is_carrier || false,
      default_rate_type: client.default_rate_type || 'per_km',
      default_rate_value: client.default_rate_value || 0, default_stop_rate: client.default_stop_rate || 0,
      fuel_surcharge_pct: client.fuel_surcharge_pct || 0,
      payment_terms_days: client.payment_terms_days ?? 30,
      rate_presets: client.rate_presets || [],
    });
    setEditId(client.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="clients-tab">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Retour
        </button>
        <Button onClick={() => { setShowForm(true); setEditId(null); setForm({name:'',email:'',phone:'',address:'',city:'',province:'QC',postal_code:'',tps_number:'',tvq_number:'',is_carrier:false,default_rate_type:'per_hour',default_rate_value:0,default_stop_rate:0,fuel_surcharge_pct:0,payment_terms_days:30,rate_presets:[]}); }} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="add-client-btn">
          <Plus size={16} className="mr-2" /> Ajouter un client
        </Button>
      </div>

      {showForm && (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4">
          <h3 className="text-white font-semibold">{editId ? 'Modifier' : 'Nouveau'} client</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'name', label: 'Nom entreprise', required: true },
              { key: 'email', label: 'Courriel' },
              { key: 'phone', label: 'Telephone' },
              { key: 'address', label: 'Adresse' },
              { key: 'city', label: 'Ville' },
              { key: 'province', label: 'Province' },
              { key: 'postal_code', label: 'Code postal' },
              { key: 'tps_number', label: 'No TPS' },
              { key: 'tvq_number', label: 'No TVQ' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-400 mb-1 block">{f.label}</label>
                <input
                  type="text"
                  value={form[f.key] || ''}
                  onChange={e => setForm(prev => ({...prev, [f.key]: e.target.value}))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  data-testid={`client-${f.key}`}
                />
              </div>
            ))}
            <div className="flex items-center gap-3 pt-5">
              <input
                type="checkbox"
                checked={form.is_carrier}
                onChange={e => setForm(prev => ({...prev, is_carrier: e.target.checked}))}
                className="rounded bg-slate-900 border-slate-600"
                data-testid="client-is-carrier"
              />
              <label className="text-sm text-white">Ce client est un transporteur (exemption de taxes)</label>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-700 pt-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Type de tarif par defaut</label>
              <select
                value={form.default_rate_type}
                onChange={e => setForm(prev => ({...prev, default_rate_type: e.target.value}))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="client-rate-type"
              >
                {rateTypes.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Tarif ($)</label>
              <input
                type="number" step="0.01" min="0"
                value={form.default_rate_value}
                onChange={e => setForm(prev => ({...prev, default_rate_value: parseFloat(e.target.value) || 0}))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="client-rate-value"
              />
            </div>
            {form.default_rate_type === 'per_km_stops' && (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Tarif par arret ($)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={form.default_stop_rate}
                  onChange={e => setForm(prev => ({...prev, default_stop_rate: parseFloat(e.target.value) || 0}))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  data-testid="client-stop-rate"
                />
              </div>
            )}
          </div>

          {/* Delai de paiement client */}
          <div className="border-t border-slate-700 pt-3">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs text-amber-400 font-semibold whitespace-nowrap">DELAI DE PAIEMENT</label>
              <div className="flex flex-wrap gap-1.5">
                {[15, 30, 45, 60, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, payment_terms_days: d }))}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      Number(form.payment_terms_days) === d
                        ? 'bg-amber-500 text-black border-amber-500 font-medium'
                        : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid={`payment-terms-preset-${d}`}
                  >
                    {d} j
                  </button>
                ))}
              </div>
              <div className="flex items-center bg-slate-900 border border-slate-700 rounded px-2 py-1 ml-auto">
                <input
                  type="number" min="0" max="365"
                  value={form.payment_terms_days ?? 30}
                  onChange={e => setForm(prev => ({ ...prev, payment_terms_days: e.target.value === '' ? null : parseInt(e.target.value, 10) || 0 }))}
                  className="w-16 bg-transparent text-sm text-white text-right focus:outline-none"
                  data-testid="payment-terms-input"
                  placeholder="30"
                />
                <span className="text-xs text-slate-400 ml-1">jours</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Date d'echeance auto-calculee sur les factures: date facture + delai
            </p>
          </div>

          {/* Rate presets */}
          <div className="border-t border-slate-700 pt-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-amber-400 font-semibold">PREREGLAGES TARIFAIRES</p>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400">Surcharge carburant</label>
                <div className="flex items-center bg-slate-900 border border-slate-700 rounded px-2 py-1">
                  <input
                    type="number" step="0.1" min="0"
                    value={form.fuel_surcharge_pct || 0}
                    onChange={e => setForm(prev => ({...prev, fuel_surcharge_pct: parseFloat(e.target.value) || 0}))}
                    className="w-16 bg-transparent text-sm text-white text-right focus:outline-none"
                    data-testid="fuel-surcharge-input"
                  />
                  <span className="text-xs text-slate-400 ml-1">%</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3">Taux de base. La surcharge s'applique aux prereglages coches.</p>
            {(form.rate_presets || []).map((preset, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center mb-2">
                <input
                  type="text"
                  value={preset.name}
                  onChange={e => {
                    const presets = [...(form.rate_presets || [])];
                    presets[idx] = { ...presets[idx], name: e.target.value };
                    setForm(prev => ({ ...prev, rate_presets: presets }));
                  }}
                  placeholder="Nom (ex: Camion seul)"
                  className="col-span-3 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-amber-500 focus:outline-none"
                />
                <select
                  value={preset.type}
                  onChange={e => {
                    const presets = [...(form.rate_presets || [])];
                    presets[idx] = { ...presets[idx], type: e.target.value };
                    setForm(prev => ({ ...prev, rate_presets: presets }));
                  }}
                  className="col-span-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white focus:border-amber-500 focus:outline-none"
                >
                  {rateTypes.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <input
                  type="number" step="0.01"
                  value={preset.value}
                  onChange={e => {
                    const presets = [...(form.rate_presets || [])];
                    presets[idx] = { ...presets[idx], value: parseFloat(e.target.value) || 0 };
                    setForm(prev => ({ ...prev, rate_presets: presets }));
                  }}
                  placeholder="Taux base"
                  className="col-span-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white text-right focus:border-amber-500 focus:outline-none"
                />
                <label className="col-span-2 flex items-center gap-1 cursor-pointer" title="Surcharge carburant applicable">
                  <input
                    type="checkbox"
                    checked={preset.surcharge_applicable !== false}
                    onChange={e => {
                      const presets = [...(form.rate_presets || [])];
                      presets[idx] = { ...presets[idx], surcharge_applicable: e.target.checked };
                      setForm(prev => ({ ...prev, rate_presets: presets }));
                    }}
                    className="accent-amber-500"
                  />
                  <span className="text-xs text-slate-400">Surch.</span>
                  {preset.surcharge_applicable !== false && (form.fuel_surcharge_pct || 0) > 0 && (
                    <span className="text-xs text-orange-400" title="La surcharge est ajoutee comme ligne separee sur la facture, pas integree au taux">
                      +{form.fuel_surcharge_pct || 0}% separee
                    </span>
                  )}
                </label>
                {preset.type === 'per_km_stops' && (
                  <input
                    type="number" step="0.01"
                    value={preset.stop_rate || 0}
                    onChange={e => {
                      const presets = [...(form.rate_presets || [])];
                      presets[idx] = { ...presets[idx], stop_rate: parseFloat(e.target.value) || 0 };
                      setForm(prev => ({ ...prev, rate_presets: presets }));
                    }}
                    placeholder="$/arret"
                    className="col-span-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white text-right focus:border-amber-500 focus:outline-none"
                  />
                )}
                <button
                  onClick={() => {
                    const presets = (form.rate_presets || []).filter((_, i) => i !== idx);
                    setForm(prev => ({ ...prev, rate_presets: presets }));
                  }}
                  className={`${preset.type === 'per_km_stops' ? 'col-span-1' : 'col-span-3'} text-red-400 hover:text-red-300 text-xs text-center`}
                >x</button>
              </div>
            ))}
            <button
              onClick={() => setForm(prev => ({ ...prev, rate_presets: [...(prev.rate_presets || []), { name: '', type: 'per_hour', value: 0, stop_rate: 0, surcharge_applicable: true }] }))}
              className="text-amber-400 hover:text-amber-300 text-xs mt-1"
            >+ Ajouter un prereglage</button>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => { setShowForm(false); setEditId(null); }}>Annuler</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-black" onClick={handleSave} data-testid="save-client-btn">Enregistrer</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {clients.map(c => (
          <div key={c.id} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 hover:border-slate-600 transition-colors cursor-pointer" onClick={() => handleEdit(c)} data-testid={`client-row-${c.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium text-sm">{c.name}</p>
                <p className="text-slate-400 text-xs">{c.email} {c.phone && `• ${c.phone}`}</p>
              </div>
              <div className="flex items-center gap-2">
                {c.is_carrier && <span className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded">Transporteur</span>}
                {(c.fuel_surcharge_pct || 0) > 0 && <span className="text-xs bg-orange-500/10 text-orange-400 px-2 py-1 rounded" data-testid={`surcharge-badge-${c.id}`}>Surch. {c.fuel_surcharge_pct}%</span>}
                {(c.rate_presets || []).length > 0 && <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded">{c.rate_presets.length} prereglage{c.rate_presets.length > 1 ? 's' : ''}</span>}
                <span className="text-xs text-slate-500">{rateTypes.find(r => r.value === c.default_rate_type)?.label} — {c.default_rate_value}$</span>
              </div>
            </div>
          </div>
        ))}
        {clients.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <Users size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucun client. Ajoutez votre premier client.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================
// TAX CONFIG TAB
// ============================
function TaxConfigTab({ get, token, onBack }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get('/billing/tax-config').then(res => {
      if (res.success) setConfig(res.data);
      setLoading(false);
    });
  }, [get]);

  const saveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/billing/tax-config`, 
        { taxes_enabled: config.taxes_enabled, taxes: config.taxes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configuration des taxes enregistree');
    } catch (e) { toast.error('Erreur'); }
  };

  const applyPreset = (preset) => {
    setConfig(prev => ({
      ...prev,
      taxes: preset.taxes.map(t => ({ ...t, enabled: true })),
    }));
  };

  if (loading || !config) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mt-12"></div>;

  return (
    <div className="space-y-4" data-testid="tax-config-tab">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">Configuration des taxes</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.taxes_enabled}
              onChange={e => setConfig(prev => ({...prev, taxes_enabled: e.target.checked}))}
              className="rounded bg-slate-900 border-slate-600"
              data-testid="taxes-enabled-toggle"
            />
            <span className="text-sm text-white">Taxes activees</span>
          </label>
        </div>

        {/* Presets */}
        <div>
          <p className="text-xs text-slate-400 mb-2">Regles preconfigurees</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {(config.presets || []).filter(p => p.id !== 'custom').map(preset => (
              <button
                key={preset.id}
                onClick={() => applyPreset(preset)}
                className="p-3 rounded-lg border border-slate-700 bg-slate-900 hover:border-amber-500/50 text-left transition-colors"
                data-testid={`tax-preset-${preset.id}`}
              >
                <p className="text-sm text-white font-medium">{preset.name}</p>
                <p className="text-xs text-slate-500 mt-1">{preset.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Custom taxes */}
        <div className="border-t border-slate-700 pt-4">
          <p className="text-xs text-slate-400 mb-2">Taxes appliquees</p>
          {config.taxes.map((tax, i) => (
            <div key={i} className="flex items-center gap-3 mb-2">
              <input
                type="checkbox"
                checked={tax.enabled !== false}
                onChange={e => {
                  const newTaxes = [...config.taxes];
                  newTaxes[i] = {...newTaxes[i], enabled: e.target.checked};
                  setConfig(prev => ({...prev, taxes: newTaxes}));
                }}
                className="rounded bg-slate-900 border-slate-600"
              />
              <input
                type="text"
                value={tax.name}
                onChange={e => {
                  const newTaxes = [...config.taxes];
                  newTaxes[i] = {...newTaxes[i], name: e.target.value};
                  setConfig(prev => ({...prev, taxes: newTaxes}));
                }}
                className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-white w-32 focus:border-amber-500 focus:outline-none"
                placeholder="Nom"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number" step="0.001" min="0" max="100"
                  value={tax.rate}
                  onChange={e => {
                    const newTaxes = [...config.taxes];
                    newTaxes[i] = {...newTaxes[i], rate: parseFloat(e.target.value) || 0};
                    setConfig(prev => ({...prev, taxes: newTaxes}));
                  }}
                  className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-white w-24 focus:border-amber-500 focus:outline-none"
                />
                <span className="text-slate-400 text-sm">%</span>
              </div>
              <button
                onClick={() => {
                  const newTaxes = config.taxes.filter((_, idx) => idx !== i);
                  setConfig(prev => ({...prev, taxes: newTaxes}));
                }}
                className="text-red-400 hover:text-red-300 text-sm"
              >Retirer</button>
            </div>
          ))}
          <button
            onClick={() => setConfig(prev => ({...prev, taxes: [...prev.taxes, { name: '', rate: 0, enabled: true }]}))}
            className="text-amber-400 hover:text-amber-300 text-sm mt-2"
          >+ Ajouter une taxe</button>
        </div>

        <Button className="bg-amber-500 hover:bg-amber-600 text-black" onClick={saveConfig} data-testid="save-tax-config-btn">
          Enregistrer la configuration
        </Button>
      </div>
    </div>
  );
}

// ============================
// INVOICE CONFIG TAB
// ============================
function InvoiceConfigTab({ get, token, onBack }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await get('/billing/invoice-config');
        if (res.success) {
          setConfig(res.data);
          if (res.data.has_logo) {
            try {
              const logoRes = await get('/billing/logo');
              if (logoRes.success) {
                setLogoPreview(`data:${logoRes.data.content_type};base64,${logoRes.data.logo_base64}`);
              }
            } catch (e) { /* no logo */ }
          }
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchConfig();
  }, [get]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/billing/invoice-config`, config, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Configuration enregistree');
    } catch (e) { toast.error('Erreur'); }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Logo trop volumineux (max 2 Mo)');

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/api/billing/logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      // Preview
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
      setConfig(prev => ({ ...prev, has_logo: true, logo_filename: file.name }));
      toast.success('Logo enregistre');
    } catch (e) { toast.error('Erreur upload'); }
    setUploading(false);
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/billing/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogoPreview(null);
      setConfig(prev => ({ ...prev, has_logo: false, logo_filename: '' }));
      toast.success('Logo supprime');
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading || !config) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mt-12"></div>;

  return (
    <div className="space-y-4" data-testid="invoice-config-tab">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-6">
        <h3 className="text-white font-semibold text-lg">Configuration de la facture</h3>
        <p className="text-xs text-slate-400">Ces informations apparaissent sur l'entete de vos factures PDF.</p>

        {/* Logo section */}
        <div className="border border-slate-700 rounded-lg p-4 space-y-3">
          <p className="text-xs text-amber-400 font-semibold">LOGO ENTREPRISE</p>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo"
                    style={{ width: `${config.invoice_logo_width || 40}mm`, maxWidth: '200px' }}
                    className="border border-slate-600 rounded bg-white p-1"
                    data-testid="logo-preview"
                  />
                  <button
                    onClick={handleLogoDelete}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-400"
                    data-testid="delete-logo-btn"
                  >x</button>
                </div>
              ) : (
                <div className="w-32 h-24 border-2 border-dashed border-slate-600 rounded-lg flex items-center justify-center">
                  <Image size={24} className="text-slate-500" />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg cursor-pointer hover:border-amber-500/50 transition-colors">
                <Upload size={14} className="text-amber-400" />
                <span className="text-xs text-white">{uploading ? 'Envoi...' : 'Telecharger un logo'}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-upload-input"
                />
              </label>
              <p className="text-xs text-slate-500">PNG, JPEG ou WebP. Max 2 Mo.</p>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Largeur du logo sur la facture (mm)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="15"
                    max="80"
                    value={config.invoice_logo_width || 40}
                    onChange={e => setConfig(prev => ({ ...prev, invoice_logo_width: parseInt(e.target.value) }))}
                    className="flex-1 accent-amber-500"
                    data-testid="logo-width-slider"
                  />
                  <span className="text-sm text-white font-medium w-12 text-right">{config.invoice_logo_width || 40} mm</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company details */}
        <div className="border border-slate-700 rounded-lg p-4 space-y-3">
          <p className="text-xs text-amber-400 font-semibold">INFORMATIONS ENTREPRISE</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'company_name', label: 'Nom entreprise' },
              { key: 'company_address', label: 'Adresse' },
              { key: 'company_phone', label: 'Telephone' },
              { key: 'company_email', label: 'Courriel' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-400 mb-1 block">{f.label}</label>
                <input
                  type="text"
                  value={config[f.key] || ''}
                  onChange={e => setConfig(prev => ({ ...prev, [f.key]: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  data-testid={`config-${f.key}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Tax numbers */}
        <div className="border border-slate-700 rounded-lg p-4 space-y-3">
          <p className="text-xs text-amber-400 font-semibold">NUMEROS DE TAXES (VENDEUR)</p>
          <p className="text-xs text-slate-500">Ces numeros apparaissent a cote des montants de taxes sur la facture PDF.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">No TPS</label>
              <input
                type="text"
                value={config.tps_number || ''}
                onChange={e => setConfig(prev => ({ ...prev, tps_number: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                placeholder="Ex: 123456789 RT0001"
                data-testid="config-tps-number"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">No TVQ</label>
              <input
                type="text"
                value={config.tvq_number || ''}
                onChange={e => setConfig(prev => ({ ...prev, tvq_number: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                placeholder="Ex: 1234567890 TQ0001"
                data-testid="config-tvq-number"
              />
            </div>
          </div>
        </div>

        <Button
          className="w-full bg-amber-500 hover:bg-amber-600 text-black"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-invoice-config-btn"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
        </Button>
      </div>
    </div>
  );
}


// ============================
// NEW INVOICE TAB
// ============================
function NewInvoiceTab({ get, post, token, clients, onDone, onBack }) {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [rateType, setRateType] = useState('per_hour');
  const [rateValue, setRateValue] = useState(0);
  const [stopRate, setStopRate] = useState(0);
  const [generating, setGenerating] = useState(false);

  const rateTypes = [
    { value: 'per_km', label: 'Au kilometre ($/km)' },
    { value: 'per_km_stops', label: 'Au km + arrets' },
    { value: 'per_trip', label: 'Au voyage (forfait)' },
    { value: 'per_hour', label: "A l'heure ($/h)" },
    { value: 'flat', label: 'Forfaitaire' },
  ];

  useEffect(() => {
    get('/billing/available-reports').then(res => {
      if (res.success) setReports(res.data);
    });
  }, [get]);

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setRateType(client.default_rate_type || 'per_km');
      setRateValue(client.default_rate_value || 0);
      setStopRate(client.default_stop_rate || 0);
    }
  };

  const handleGenerate = async () => {
    if (!selectedReport) return toast.error('Selectionnez un rapport');
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/billing/invoices/generate`, {
        report_id: selectedReport,
        client_id: selectedClient || null,
        rate_type: rateType,
        rate_value: rateValue,
        stop_rate: stopRate,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const invoices = Array.isArray(res.data) ? res.data : [res.data];
      if (invoices.length === 1) {
        toast.success(`Facture ${invoices[0].invoice_number} creee (${invoices[0].total?.toFixed(2)}$)`);
      } else {
        const totalAll = invoices.reduce((s, i) => s + (i.total || 0), 0);
        toast.success(`${invoices.length} factures creees (total: ${totalAll.toFixed(2)}$)`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setGenerating(false);
  };

  const selectedReportData = reports.find(r => r.id === selectedReport);

  return (
    <div className="space-y-4" data-testid="new-invoice-tab">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-5">
        <h3 className="text-white font-semibold">Generer une facture depuis un rapport</h3>

        {/* Report selection */}
        <div>
          <label className="text-xs text-slate-400 mb-2 block">Rapport journalier</label>
          {reports.length === 0 ? (
            <p className="text-sm text-slate-500">Aucun rapport complete disponible pour facturation.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {reports.map(r => (
                <button
                  key={r.id}
                  onClick={() => setSelectedReport(r.id)}
                  className={`w-full p-3 rounded-lg border text-left transition-colors ${
                    selectedReport === r.id ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                  }`}
                  data-testid={`report-${r.id}`}
                >
                  <div className="flex justify-between">
                    <div>
                      <span className="text-sm text-white font-medium">{r.report_number}</span>
                      <span className="text-xs text-slate-500 ml-2">{r.date}</span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {r.total_hours ? `${r.total_hours}h` : ''}{r.total_hours && r.total_km ? ' • ' : ''}{r.total_km ? `${r.total_km} km` : ''}{(r.total_hours || r.total_km) ? ' • ' : ''}{r.num_stops || 0} arrets
                    </span>
                  </div>
                  {r.client_names?.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">{r.client_names.join(', ')}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected report summary */}
        {selectedReportData && (
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4" data-testid="report-summary">
            <p className="text-xs text-amber-400 font-semibold mb-2">RESUME DU RAPPORT {selectedReportData.report_number}</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-800 rounded p-2">
                <p className="text-lg font-bold text-white">{selectedReportData.total_hours || '—'}</p>
                <p className="text-xs text-slate-400">Heures payables</p>
              </div>
              <div className="bg-slate-800 rounded p-2">
                <p className="text-lg font-bold text-white">{selectedReportData.total_km || '—'}</p>
                <p className="text-xs text-slate-400">Kilometres (GPS)</p>
              </div>
              <div className="bg-slate-800 rounded p-2">
                <p className="text-lg font-bold text-white">{selectedReportData.num_stops || 0}</p>
                <p className="text-xs text-slate-400">Arrets</p>
              </div>
            </div>
            {selectedReportData.client_names?.length > 0 && (
              <p className="text-xs text-slate-400 mt-2">Clients: {selectedReportData.client_names.join(' → ')}</p>
            )}
          </div>
        )}

        {/* Client selection */}
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Client (destinataire)</label>
          <select
            value={selectedClient}
            onChange={e => handleClientChange(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-amber-500 focus:outline-none"
            data-testid="invoice-client-select"
          >
            <option value="">-- Selectionnez un client --</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name} {c.is_carrier ? '(Transporteur)' : ''}</option>
            ))}
          </select>
        </div>

        {/* Rate config */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Type de tarif</label>
            <select
              value={rateType}
              onChange={e => setRateType(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="invoice-rate-type"
            >
              {rateTypes.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">
              {rateType === 'per_hour' ? 'Tarif ($/h)' : rateType === 'per_km' || rateType === 'per_km_stops' ? 'Tarif ($/km)' : 'Tarif ($)'}
            </label>
            <input
              type="number" step="0.01" min="0"
              value={rateValue}
              onChange={e => setRateValue(parseFloat(e.target.value) || 0)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="invoice-rate-value"
            />
          </div>
          {rateType === 'per_km_stops' && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Tarif par arret ($)</label>
              <input
                type="number" step="0.01" min="0"
                value={stopRate}
                onChange={e => setStopRate(parseFloat(e.target.value) || 0)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="invoice-stop-rate"
              />
            </div>
          )}
        </div>

        {/* Dynamic preview */}
        {selectedReportData && rateValue > 0 && (
          <div className="bg-slate-900 border border-amber-500/30 rounded-lg p-4" data-testid="invoice-preview">
            <p className="text-xs text-amber-400 font-semibold mb-2">APERCU DU CALCUL</p>
            {(() => {
              const h = selectedReportData.total_hours || 0;
              const km = selectedReportData.total_km || 0;
              const stops = selectedReportData.num_stops || 0;
              let previewQty = 0;
              let previewUnit = '';
              let previewTotal = 0;
              let extra = null;

              if (rateType === 'per_hour') {
                previewQty = h; previewUnit = 'h'; previewTotal = h * rateValue;
              } else if (rateType === 'per_km') {
                previewQty = km; previewUnit = 'km'; previewTotal = km * rateValue;
              } else if (rateType === 'per_km_stops') {
                previewQty = km; previewUnit = 'km'; previewTotal = km * rateValue;
                if (stopRate > 0) { extra = `+ ${stops} arrets x ${stopRate.toFixed(2)}$ = ${(stops * stopRate).toFixed(2)}$`; previewTotal += stops * stopRate; }
              } else if (rateType === 'per_trip' || rateType === 'flat') {
                previewQty = 1; previewUnit = 'voyage'; previewTotal = rateValue;
              }

              return (
                <div>
                  <p className="text-white text-sm">
                    <span className="text-lg font-bold">{previewQty}</span> {previewUnit} x <span className="text-lg font-bold">{rateValue.toFixed(2)}</span>$ = <span className="text-lg font-bold text-amber-400">{previewTotal.toFixed(2)}$</span>
                  </p>
                  {extra && <p className="text-white text-sm mt-1">{extra}</p>}
                  {rateType === 'per_hour' && !h && <p className="text-red-400 text-xs mt-1">Aucune heure payable dans ce rapport</p>}
                  {(rateType === 'per_km' || rateType === 'per_km_stops') && !km && <p className="text-red-400 text-xs mt-1">Aucun kilometrage dans ce rapport</p>}
                </div>
              );
            })()}
          </div>
        )}

        {selectedReportData && rateValue === 0 && (
          <p className="text-red-400 text-xs text-center">Entrez un tarif superieur a 0$ pour voir l'apercu</p>
        )}

        <Button
          className="w-full bg-amber-500 hover:bg-amber-600 text-black"
          onClick={handleGenerate}
          disabled={!selectedReport || generating}
          data-testid="generate-invoice-btn"
        >
          {generating ? 'Generation...' : 'Generer la facture brouillon'}
        </Button>
      </div>
    </div>
  );
}

// ============================
// INVOICE DETAIL TAB
// ============================
function InvoiceDetailTab({ invoiceId, get, post, token, clients, onBack, statusConfig }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [appliedPresetName, setAppliedPresetName] = useState(null);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [pendingSave, setPendingSave] = useState(null);

  const rateTypes = [
    { value: 'per_hour', label: "A l'heure ($/h)" },
    { value: 'per_km', label: 'Au kilometre ($/km)' },
    { value: 'per_km_stops', label: 'Au km + arrets' },
    { value: 'per_trip', label: 'Au voyage' },
    { value: 'flat', label: 'Forfaitaire' },
  ];

  useEffect(() => {
    get(`/billing/invoices/${invoiceId}`).then(res => {
      if (res.success) setInvoice(res.data);
      setLoading(false);
    });
  }, [invoiceId, get]);

  const updateClientPresets = async (clientId, presets) => {
    try {
      await axios.put(`${API_URL}/api/billing/clients/${clientId}`, 
        { rate_presets: presets },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) { /* silent */ }
  };

  const saveInvoice = async (updates) => {
    const dataToSave = updates || invoice;
    
    // Check if we should update/create presets for the client
    if (!updates && invoice?.client_id) {
      const client = clients.find(c => c.id === invoice.client_id);
      if (client) {
        const currentPresets = client.rate_presets || [];
        const currentRate = invoice.rate_type;
        const mainLine = (invoice.line_items || []).find(i => !i.description?.startsWith('Supplement arrets'));
        const currentPrice = mainLine?.unit_price || 0;
        const stopLine = (invoice.line_items || []).find(i => i.description?.startsWith('Supplement arrets'));
        const currentStopRate = stopLine?.unit_price || 0;

        if (currentPrice > 0) {
          // La surcharge est maintenant une ligne separee : le unit_price EST le taux de base.
          // Pas besoin de back-calculer.

          // Check if an applied preset has been modified
          if (appliedPresetName) {
            const preset = currentPresets.find(p => p.name === appliedPresetName);
            if (preset && (preset.value !== currentPrice || preset.type !== currentRate ||
                (currentRate === 'per_km_stops' && (preset.stop_rate || 0) !== currentStopRate))) {
              if (window.confirm(`Le tarif a change. Mettre a jour le prereglage "${appliedPresetName}" ?\n\nAncien taux: ${preset.value}$\nNouveau taux: ${currentPrice}$`)) {
                const updatedPresets = currentPresets.map(p =>
                  p.name === appliedPresetName
                    ? { ...p, type: currentRate, value: currentPrice, stop_rate: currentStopRate }
                    : p
                );
                await updateClientPresets(invoice.client_id, updatedPresets);
                client.rate_presets = updatedPresets;
                toast.success(`Prereglage "${appliedPresetName}" mis a jour`);
              }
            }
          } else {
            // No preset was applied — check if one matches (base rate direct)
            const matchingPreset = currentPresets.find(p => {
              return p.type === currentRate && p.value === currentPrice &&
                (currentRate !== 'per_km_stops' || (p.stop_rate || 0) === currentStopRate);
            });
            if (!matchingPreset) {
              // No matching preset exists — propose to create one
              setNewPresetName('');
              setPendingSave(dataToSave);
              setShowPresetDialog(true);
              return; // Wait for dialog response
            }
          }
        }
      }
    }

    await doSave(dataToSave);
  };

  const doSave = async (dataToSave) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/billing/invoices/${invoiceId}`, 
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoice(res.data);
      toast.success('Facture sauvegardee');
    } catch (e) { toast.error('Erreur'); }
    setSaving(false);
  };

  const handlePresetDialogSave = async (createPreset) => {
    if (createPreset && newPresetName.trim() && invoice?.client_id) {
      const client = clients.find(c => c.id === invoice.client_id);
      if (client) {
        const mainLine = (invoice.line_items || []).find(i => !i.description?.startsWith('Supplement arrets'));
        const stopLine = (invoice.line_items || []).find(i => i.description?.startsWith('Supplement arrets'));
        const newPreset = {
          name: newPresetName.trim(),
          type: invoice.rate_type,
          value: mainLine?.unit_price || 0,
          stop_rate: stopLine?.unit_price || 0,
          surcharge_applicable: true,
        };
        const updatedPresets = [...(client.rate_presets || []), newPreset];
        await updateClientPresets(invoice.client_id, updatedPresets);
        client.rate_presets = updatedPresets;
        toast.success(`Prereglage "${newPresetName}" cree pour ${client.name}`);
      }
    }
    setShowPresetDialog(false);
    if (pendingSave) {
      await doSave(pendingSave);
      setPendingSave(null);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/billing/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_${invoice?.custom_invoice_number || invoice?.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur telechargement'); }
  };

  const deleteInvoice = async () => {
    if (!window.confirm('Supprimer definitivement cette facture ?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/billing/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Facture supprimee');
      onBack();
    } catch (e) { toast.error('Erreur suppression'); }
    setDeleting(false);
  };

  const sendInvoice = async () => {
    if (!invoice?.client_email) return toast.error('Le client n\'a pas d\'adresse email');
    try {
      const res = await axios.post(`${API_URL}/api/billing/invoices/${invoiceId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      setInvoice(prev => ({ ...prev, status: 'sent', sent_at: new Date().toISOString() }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
  };

  const markPaid = () => saveInvoice({ status: 'paid', payment_date: new Date().toISOString().slice(0, 10) });

  const updateLineItem = (index, field, value) => {
    const items = [...(invoice.line_items || [])];
    items[index] = { ...items[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      items[index].amount = Math.round((items[index].quantity || 0) * (items[index].unit_price || 0) * 100) / 100;
    }
    setInvoice(prev => ({ ...prev, line_items: items }));
  };

  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), { description: '', quantity: 1, unit_price: 0, amount: 0 }],
    }));
  };

  // Switch billing mode — update quantities, reset unit_price to avoid confusion
  const switchMode = (newMode) => {
    if (!invoice) return;
    let items = [...(invoice.line_items || [])];
    const hours = invoice.total_hours || 0;
    const km = invoice.total_km || 0;
    const stops = invoice.num_stops || 0;

    // Remove old supplement arrets line when switching away from per_km_stops
    if (newMode !== 'per_km_stops') {
      items = items.filter(i => !i.description?.startsWith('Supplement arrets'));
    }

    // Count only main lines (not supplement lines)
    const mainLines = items.filter(i => i.description && !i.description.startsWith('Supplement arrets'));
    const numLines = mainLines.length || 1;

    items.forEach(item => {
      // Skip supplement arrets lines — they have their own logic below
      if (item.description?.startsWith('Supplement arrets')) return;

      if (newMode === 'per_hour') {
        item.quantity = Math.round((hours / numLines) * 100) / 100;
      } else if (newMode === 'per_km' || newMode === 'per_km_stops') {
        item.quantity = Math.round((km / numLines) * 10) / 10;
      } else {
        item.quantity = 1;
      }
      // Reset unit_price when switching modes to avoid showing $/h as $/km
      item.unit_price = 0;
      item.amount = 0;
    });

    // For per_km_stops, add or update stop supplement line
    if (newMode === 'per_km_stops') {
      const existingIdx = items.findIndex(i => i.description?.startsWith('Supplement arrets'));
      if (existingIdx === -1 && stops > 0) {
        items.push({ description: `Supplement arrets (${stops})`, quantity: stops, unit_price: 0, amount: 0 });
      }
    }

    setInvoice(prev => ({ ...prev, rate_type: newMode, line_items: items }));
    setAppliedPresetName(null); // Manual mode change, no preset tracked
  };

  // Apply a rate preset — sets mode + fills unit_price on all lines
  // La surcharge carburant n'est PLUS integree au taux : elle apparait comme
  // une ligne separee dans les totaux. On marque juste les lignes eligibles
  // via item.surcharge_applicable pour que le backend calcule la surcharge.
  const applyPreset = (preset) => {
    const items = [...(invoice.line_items || [])];
    const numLines = items.filter(i => i.description && !i.description.startsWith('Supplement arrets')).length || 1;
    const hours = invoice.total_hours || 0;
    const km = invoice.total_km || 0;
    const newMode = preset.type;

    const client = clients.find(c => c.id === invoice.client_id);
    const presetSurchargeApplicable = preset.surcharge_applicable !== false;
    const clientPct = client?.fuel_surcharge_pct || 0;
    const baseRate = preset.value || 0;

    items.forEach(item => {
      if (!item.description?.startsWith('Supplement arrets')) {
        if (newMode === 'per_hour') {
          item.quantity = numLines > 0 ? Math.round((hours / numLines) * 100) / 100 : hours;
        } else if (newMode === 'per_km' || newMode === 'per_km_stops') {
          item.quantity = numLines > 0 ? Math.round((km / numLines) * 10) / 10 : km;
        } else {
          item.quantity = 1;
        }
        item.unit_price = baseRate;
        item.amount = Math.round(item.quantity * item.unit_price * 100) / 100;
        item.surcharge_applicable = presetSurchargeApplicable;
      }
    });

    // Handle stop supplement for per_km_stops
    if (newMode === 'per_km_stops') {
      const stops = invoice.num_stops || 0;
      const stopRate = preset.stop_rate || 0;
      const existingIdx = items.findIndex(i => i.description?.startsWith('Supplement arrets'));
      if (existingIdx >= 0) {
        items[existingIdx].unit_price = stopRate;
        items[existingIdx].amount = Math.round(stops * stopRate * 100) / 100;
        items[existingIdx].surcharge_applicable = presetSurchargeApplicable;
      } else if (stops > 0) {
        items.push({
          description: `Supplement arrets (${stops})`,
          quantity: stops,
          unit_price: stopRate,
          amount: Math.round(stops * stopRate * 100) / 100,
          surcharge_applicable: presetSurchargeApplicable,
        });
      }
    }

    setInvoice(prev => ({
      ...prev,
      rate_type: newMode,
      rate_value: baseRate,
      line_items: items,
      fuel_surcharge_pct: presetSurchargeApplicable ? clientPct : (prev.fuel_surcharge_pct || 0),
    }));
    setAppliedPresetName(preset.name);
    const msg = presetSurchargeApplicable && clientPct > 0
      ? `Prereglage "${preset.name}" applique (${baseRate}$ + ${clientPct}% surcharge separee)`
      : `Prereglage "${preset.name}" applique`;
    toast.success(msg);
  };

  if (loading || !invoice) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mt-12"></div>;

  const sc = statusConfig[invoice.status] || statusConfig.draft;
  const StatusIcon = sc.icon;
  const subtotal = (invoice.line_items || []).reduce((s, item) => s + (item.amount || 0), 0);
  const selectedClient = clients.find(c => c.id === invoice.client_id);
  const clientPresets = selectedClient?.rate_presets || [];

  // Extract equipment from stops summary
  const equipments = [...new Set((invoice.stops_summary || []).map(s => s.equipment_used).filter(e => e && e !== 'none'))];
  const equipLabels = { moffett: 'Moffett', transpalette: 'Transpalette', lift: 'Lift' };

  return (
    <div className="space-y-4" data-testid="invoice-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" onClick={downloadPdf} data-testid="download-pdf-btn">
            <Download size={16} className="mr-2" /> PDF
          </Button>
          {invoice.status === 'draft' && (
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={sendInvoice} data-testid="send-invoice-btn">
              <Send size={16} className="mr-2" /> Envoyer
            </Button>
          )}
          {invoice.status === 'sent' && (
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={markPaid} data-testid="mark-paid-btn">
              <CheckCircle size={16} className="mr-2" /> Marquer payee
            </Button>
          )}
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-5">
        {/* Invoice header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Facture {invoice.custom_invoice_number || invoice.invoice_number}</h2>
            <p className="text-slate-400 text-sm">{invoice.date} — Chauffeur: {invoice.driver_name}</p>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${sc.bg} ${sc.color}`}>
            <StatusIcon size={14} /> {sc.label}
          </span>
        </div>

        {/* Report data panel */}
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4" data-testid="report-data-panel">
          <p className="text-xs text-amber-400 font-semibold mb-3">DONNEES DU RAPPORT</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="bg-slate-800 rounded p-2">
              <p className="text-lg font-bold text-white">{invoice.total_hours || '—'}</p>
              <p className="text-xs text-slate-400">Heures payables</p>
            </div>
            <div className="bg-slate-800 rounded p-2">
              <p className="text-lg font-bold text-white">{invoice.total_km || '—'}</p>
              <p className="text-xs text-slate-400">Kilometres</p>
            </div>
            <div className="bg-slate-800 rounded p-2">
              <p className="text-lg font-bold text-white">{invoice.num_stops || 0}</p>
              <p className="text-xs text-slate-400">Arrets</p>
            </div>
            <div className="bg-slate-800 rounded p-2">
              <p className="text-lg font-bold text-white">{equipments.length > 0 ? equipments.map(e => equipLabels[e] || e).join(', ') : '—'}</p>
              <p className="text-xs text-slate-400">Equipement</p>
            </div>
          </div>
        </div>

        {/* Mode selector */}
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-4" data-testid="mode-selector">
          <p className="text-xs text-amber-400 font-semibold mb-3">MODE DE FACTURATION</p>
          <div className="flex flex-wrap gap-2">
            {rateTypes.map(rt => (
              <button
                key={rt.value}
                onClick={() => switchMode(rt.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  invoice.rate_type === rt.value
                    ? 'bg-amber-500 text-black'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-600'
                }`}
                data-testid={`mode-${rt.value}`}
              >
                {rt.label}
              </button>
            ))}
          </div>

          {/* AI suggestion banner (visible when the invoice was auto-generated) */}
          {invoice.ai_suggested_preset && invoice.status === 'draft' && (
            <div className="mt-3 pt-3 border-t border-slate-700" data-testid="ai-suggestion-banner">
              <div className="flex items-start gap-2 p-2 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <Sparkles size={14} className="text-purple-400 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <div className="text-purple-300 font-medium">
                    🤖 Préréglage suggéré : <span className="text-white">{invoice.ai_suggested_preset}</span>
                    {invoice.ai_suggestion_source && (
                      <span className="ml-2 text-purple-400/70">
                        (via {invoice.ai_suggestion_source === 'cache' ? 'apprentissage' : invoice.ai_suggestion_source === 'heuristic' ? 'analyse' : 'défaut'} · {invoice.ai_suggestion_confidence || 0}%)
                      </span>
                    )}
                  </div>
                  {invoice.equipment_signature && (
                    <div className="text-slate-400 mt-0.5">
                      Équipement détecté : <span className="text-slate-200">{invoice.equipment_signature.replace(/\+/g, ' + ')}</span>
                    </div>
                  )}
                  <div className="text-slate-500 text-[10px] mt-1">
                    Change le préréglage ci-dessous si nécessaire — le système apprendra pour la prochaine fois.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Client rate presets */}
          {clientPresets.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <p className="text-xs text-slate-400 mb-2">Prereglages tarifaires — {selectedClient?.name}</p>
              <div className="flex flex-wrap gap-2">
                {clientPresets.map((preset, idx) => {
                  const surchargeApplies = preset.surcharge_applicable !== false && (selectedClient?.fuel_surcharge_pct || 0) > 0;
                  const isSuggested = invoice.ai_suggested_preset === preset.name;
                  return (
                    <button
                      key={idx}
                      onClick={async () => {
                        applyPreset(preset);
                        // If user chose a different preset than the AI suggestion on a draft
                        // -> teach the system for next time (silent)
                        if (invoice.status === 'draft' && invoice.equipment_signature &&
                            invoice.ai_suggested_preset && invoice.ai_suggested_preset !== preset.name && invoice.id) {
                          try {
                            await post(`/billing/invoices/${invoice.id}/learn-preset-choice`, { preset_name: preset.name });
                          } catch (_) { /* silent */ }
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        isSuggested
                          ? 'bg-purple-500/15 border-purple-500/50 text-purple-200 ring-1 ring-purple-500/40'
                          : 'bg-slate-800 border-amber-500/30 text-amber-300 hover:bg-amber-500/10'
                      }`}
                      data-testid={`preset-${idx}`}
                      title={surchargeApplies
                        ? `${preset.value}$ + surcharge carburant ${selectedClient.fuel_surcharge_pct}% (ligne separee)`
                        : `${preset.type === 'per_hour' ? '$/h' : preset.type === 'per_km' ? '$/km' : '$'}: ${preset.value}`
                      }
                    >
                      {isSuggested && <Sparkles size={10} className="inline mr-1" />}
                      {preset.name} — {preset.value}$
                      {surchargeApplies && <span className="ml-1 text-orange-400">+{selectedClient.fuel_surcharge_pct}%</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Client info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-slate-900 rounded-lg">
          <div>
            <p className="text-xs text-slate-500 mb-1">Client</p>
            <select
              value={invoice.client_id || ''}
              onChange={e => {
                const client = clients.find(c => c.id === e.target.value);
                setInvoice(prev => ({
                  ...prev,
                  client_id: e.target.value,
                  client_name: client?.name || '',
                  client_email: client?.email || '',
                  client_address: client ? `${client.address} ${client.city} ${client.province} ${client.postal_code}`.trim() : '',
                }));
              }}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="detail-client-select"
            >
              <option value="">-- Selectionnez --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">No facture personnalise</p>
            <input
              type="text"
              value={invoice.custom_invoice_number || ''}
              onChange={e => setInvoice(prev => ({...prev, custom_invoice_number: e.target.value}))}
              placeholder={invoice.invoice_number}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="detail-custom-number"
            />
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-white font-medium">Lignes de facturation</p>
            <p className="text-xs text-slate-500">
              {invoice.rate_type === 'per_hour' ? 'Qte = heures' : invoice.rate_type === 'per_km' || invoice.rate_type === 'per_km_stops' ? 'Qte = km' : 'Qte = unite'}
            </p>
          </div>
          <div className="grid grid-cols-12 gap-2 mb-1 px-1">
            <span className="col-span-5 text-xs text-slate-500">Description</span>
            <span className="col-span-2 text-xs text-slate-500 text-right">Qte</span>
            <span className="col-span-2 text-xs text-slate-500 text-right">Prix unit.</span>
            <span className="col-span-2 text-xs text-slate-500 text-right">Montant</span>
            <span className="col-span-1"></span>
          </div>
          <div className="space-y-2">
            {(invoice.line_items || []).map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  value={item.description}
                  onChange={e => updateLineItem(i, 'description', e.target.value)}
                  className="col-span-5 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  placeholder="Description"
                />
                <input
                  type="number" step="0.01"
                  value={item.quantity}
                  onChange={e => updateLineItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                  className="col-span-2 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white text-right focus:border-amber-500 focus:outline-none"
                />
                <input
                  type="number" step="0.01"
                  value={item.unit_price}
                  onChange={e => updateLineItem(i, 'unit_price', parseFloat(e.target.value) || 0)}
                  className="col-span-2 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white text-right focus:border-amber-500 focus:outline-none"
                />
                <span className="col-span-2 text-sm text-white text-right font-medium">{item.amount?.toFixed(2)} $</span>
                <button
                  onClick={() => setInvoice(prev => ({...prev, line_items: prev.line_items.filter((_, idx) => idx !== i)}))}
                  className="col-span-1 text-red-400 hover:text-red-300 text-center"
                >x</button>
              </div>
            ))}
          </div>
          <button onClick={addLineItem} className="text-amber-400 hover:text-amber-300 text-sm mt-2">+ Ajouter une ligne</button>
        </div>

        {/* Totals */}
        <div className="border-t border-slate-700 pt-4 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Sous-total</span>
            <span className="text-white" data-testid="invoice-subtotal">{subtotal.toFixed(2)} $</span>
          </div>
          {(() => {
            const fuelPct = invoice.fuel_surcharge_pct || 0;
            const surchargeableBase = (invoice.line_items || [])
              .filter(i => i.surcharge_applicable !== false)
              .reduce((s, i) => s + (i.amount || 0), 0);
            const fuelAmount = Math.round(surchargeableBase * fuelPct / 100 * 100) / 100;
            if (fuelAmount <= 0) return null;
            return (
              <div className="flex justify-between items-center text-sm" data-testid="invoice-fuel-surcharge">
                <span className="text-orange-400">Surcharge de carburant cette semaine {fuelPct}%</span>
                <span className="text-white" data-testid="invoice-fuel-amount">{fuelAmount.toFixed(2)} $</span>
              </div>
            );
          })()}
          {(() => {
            const fuelPct = invoice.fuel_surcharge_pct || 0;
            const surchargeableBase = (invoice.line_items || [])
              .filter(i => i.surcharge_applicable !== false)
              .reduce((s, i) => s + (i.amount || 0), 0);
            const fuelAmount = Math.round(surchargeableBase * fuelPct / 100 * 100) / 100;
            const taxableBase = subtotal + fuelAmount;
            const taxes = invoice.taxes_enabled ? (invoice.applied_taxes || []) : [];
            const taxTotal = taxes.reduce((s, t) => s + taxableBase * t.rate / 100, 0);
            return (
              <>
                {taxes.map((tax, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-slate-400">{tax.name} ({tax.rate}%)</span>
                    <span className="text-white">{(taxableBase * tax.rate / 100).toFixed(2)} $</span>
                  </div>
                ))}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700">
                  <span className="text-white">Total</span>
                  <span className="text-amber-400" data-testid="invoice-total">
                    {(taxableBase + taxTotal).toFixed(2)} $
                  </span>
                </div>
              </>
            );
          })()}
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Notes</label>
          <textarea
            value={invoice.notes || ''}
            onChange={e => setInvoice(prev => ({...prev, notes: e.target.value}))}
            rows={2}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none resize-none"
            data-testid="invoice-notes"
          />
        </div>

        {/* Preset creation dialog */}
        {showPresetDialog && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 space-y-3" data-testid="preset-dialog">
            <p className="text-sm text-amber-400 font-semibold">Nouveau prereglage tarifaire</p>
            <p className="text-xs text-slate-400">
              Ce tarif ({invoice?.rate_type === 'per_hour' ? 'horaire' : invoice?.rate_type === 'per_km' ? 'au km' : invoice?.rate_type} 
              {' '}a {(invoice?.line_items || []).find(i => !i.description?.startsWith('Supplement'))?.unit_price || 0}$) 
              n'existe pas dans les prereglages de {selectedClient?.name}. 
              Voulez-vous le sauvegarder pour usage futur ?
            </p>
            <input
              type="text"
              value={newPresetName}
              onChange={e => setNewPresetName(e.target.value)}
              placeholder="Nom du prereglage (ex: Horaire surcharge carburant)"
              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="preset-name-input"
              autoFocus
            />
            <div className="flex gap-2">
              <Button 
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black text-sm"
                onClick={() => handlePresetDialogSave(true)}
                disabled={!newPresetName.trim()}
                data-testid="preset-create-btn"
              >
                Creer et sauvegarder
              </Button>
              <Button 
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300 text-sm"
                onClick={() => handlePresetDialogSave(false)}
                data-testid="preset-skip-btn"
              >
                Sauvegarder sans prereglage
              </Button>
            </div>
          </div>
        )}

        <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" onClick={() => saveInvoice()} disabled={saving} data-testid="save-invoice-btn">
          {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
        </Button>
        <Button 
          variant="outline" 
          className="w-full border-red-800 text-red-400 hover:bg-red-900/30 mt-2" 
          onClick={deleteInvoice} 
          disabled={deleting}
          data-testid="delete-invoice-btn"
        >
          <Trash2 size={16} className="mr-2" />
          {deleting ? 'Suppression...' : 'Supprimer cette facture'}
        </Button>
      </div>
    </div>
  );
}
